import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../firebase/config';

export interface Transaction {
  amount: string;
  amountValue: number;
  businessId: string;
  createdBy: string;
  customerId: string;
  storeName: string;
  timestamp: Date;
  type: 'earned' | 'redeemed';
}

export const getTransactions = async (userId: string): Promise<Transaction[]> => {
  try {
    const transactionsRef = collection(db, "transactions");
    const q = query(transactionsRef, where("customerId", "==", userId));
    
    const querySnapshot = await getDocs(q);
    const transactions: Transaction[] = [];
    
    querySnapshot.forEach((doc) => {
      const data = doc.data();
      transactions.push({
        ...data,
        timestamp: data.timestamp?.toDate(), // Convert Firestore timestamp to JS Date
      } as Transaction);
    });
    
    return transactions;
  } catch (error) {
    console.error("Error fetching transactions:", error);
    throw error;
  }
};

// Calculate total store credit for a user
export const calculateStoreCredit = (transactions: Transaction[]): number => {
  return transactions.reduce((total, transaction) => {
    if (transaction.type === 'earned') {
      return total + transaction.amountValue;
    } else {
      return total - transaction.amountValue;
    }
  }, 0);
};

// Get unique businesses where user has credit
export const getBusinessesWithCredit = (transactions: Transaction[]): string[] => {
  // Create a map of business IDs to their net credit
  const businessCreditMap = new Map<string, number>();
  
  transactions.forEach(transaction => {
    const currentCredit = businessCreditMap.get(transaction.businessId) || 0;
    
    if (transaction.type === 'earned') {
      businessCreditMap.set(transaction.businessId, currentCredit + transaction.amountValue);
    } else {
      businessCreditMap.set(transaction.businessId, currentCredit - transaction.amountValue);
    }
  });
  
  // Filter businesses with positive credit balance
  return Array.from(businessCreditMap.entries())
    .filter(([_, credit]) => credit > 0)
    .map(([businessId, _]) => businessId);
};